import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Clock, Video, MessageSquare, Calendar, User, DollarSign, Award, ThumbsUp, Loader } from 'lucide-react';
import useExpertStore from '../store/expertStore';
import ReviewForm from '../components/ReviewForm';
import { submitReview, bookConsultation } from '../services/expertService';
import { scheduleConsultationReminder } from '../services/notificationService';

const ExpertConnectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { experts, selectedExpert, loading, error, loadExperts, selectExpert } = useExpertStore();
  const [consultationType, setConsultationType] = useState<'video' | 'chat'>('video');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [bookingError, setBookingError] = useState<string | null>(null);

  useEffect(() => {
    loadExperts();
  }, []);

  const handleExpertSelect = (expert: any) => {
    selectExpert(expert);
    setBookingError(null);
  };

  const handleStartConsultation = () => {
    if (!selectedExpert) {
      setBookingError('Please select an expert first');
      return;
    }

    const route = consultationType === 'video' ? 'video-call' : 'chat';
    navigate(`/${route}/${selectedExpert.id}`);
  };

  const handleConsultationTypeChange = (type: 'video' | 'chat') => {
    setConsultationType(type);
  };

  const handleBookConsultation = async () => {
    if (!selectedExpert || !selectedDate || !selectedTime) {
      setBookingError('Please select a date and time for the consultation');
      return;
    }

    try {
      setBookingError(null);
      const scheduledAt = new Date(`${selectedDate}T${selectedTime}`);
      
      if (scheduledAt < new Date()) {
        setBookingError('Please select a future date and time');
        return;
      }

      const consultation = await bookConsultation(
        selectedExpert.id,
        scheduledAt,
        consultationType
      );

      // Schedule a reminder
      scheduleConsultationReminder(consultation);

      // Navigate to the appropriate page based on consultation type
      handleStartConsultation();
    } catch (error) {
      console.error('Error booking consultation:', error);
      setBookingError('Failed to book consultation. Please try again.');
    }
  };

  const handleSubmitReview = async (rating: number, comment: string) => {
    if (!selectedExpert) return;
    
    try {
      await submitReview(selectedExpert.id, rating, comment);
      await loadExperts(); // Reload experts to update ratings
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-destructive">
        Error loading experts: {error}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <User className="mr-2 text-primary" />
          Expert Connection
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Expert List */}
          <div className="lg:col-span-1 space-y-4">
            {experts.map(expert => (
              <button
                key={expert.id}
                onClick={() => handleExpertSelect(expert)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  selectedExpert?.id === expert.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold">{expert.full_name}</h3>
                    <p className="text-sm text-muted-foreground">{expert.title}</p>
                  </div>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="ml-1">{expert.rating.toFixed(1)}</span>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <p className="flex items-center text-muted-foreground">
                    <Award className="w-4 h-4 mr-1" />
                    {expert.specialization}
                  </p>
                  <p className="flex items-center text-muted-foreground">
                    <DollarSign className="w-4 h-4 mr-1" />
                    ${expert.hourly_rate}/hour
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Expert Details & Booking */}
          <div className="lg:col-span-2">
            {selectedExpert ? (
              <div className="space-y-6">
                <div className="bg-card/30 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold">{selectedExpert.full_name}</h3>
                      <p className="text-muted-foreground">{selectedExpert.title}</p>
                    </div>
                    <div className="flex items-center">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className="ml-1">{selectedExpert.rating.toFixed(1)}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm">{selectedExpert.bio}</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <Award className="w-5 h-5 text-primary mr-2" />
                        <div>
                          <p className="font-medium">Specialization</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedExpert.specialization}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-primary mr-2" />
                        <div>
                          <p className="font-medium">Experience</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedExpert.experience_years} years
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booking Form */}
                <div className="bg-card/30 rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Book a Consultation</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Consultation Type
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => handleConsultationTypeChange('video')}
                          className={`flex items-center justify-center p-3 rounded-lg border ${
                            consultationType === 'video'
                              ? 'border-primary bg-primary/5'
                              : 'border-border'
                          }`}
                        >
                          <Video className="w-5 h-5 mr-2" />
                          Video Call
                        </button>
                        
                        <button
                          onClick={() => handleConsultationTypeChange('chat')}
                          className={`flex items-center justify-center p-3 rounded-lg border ${
                            consultationType === 'chat'
                              ? 'border-primary bg-primary/5'
                              : 'border-border'
                          }`}
                        >
                          <MessageSquare className="w-5 h-5 mr-2" />
                          Chat
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full p-2 bg-card border border-border rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="w-full p-2 bg-card border border-border rounded-lg"
                      />
                    </div>

                    {bookingError && (
                      <div className="text-sm text-destructive">
                        {bookingError}
                      </div>
                    )}

                    <div className="space-y-4">
                      <button
                        onClick={handleStartConsultation}
                        className="w-full bg-primary text-primary-foreground py-2 rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Start {consultationType === 'video' ? 'Video Call' : 'Chat'} Now
                      </button>

                      <button
                        onClick={handleBookConsultation}
                        className="w-full bg-secondary text-secondary-foreground py-2 rounded-lg hover:bg-secondary/90 transition-colors"
                      >
                        Schedule for Later (${selectedExpert.hourly_rate}/hour)
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reviews */}
                <div className="bg-card/30 rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Reviews</h4>
                  
                  <div className="space-y-4">
                    {selectedExpert.reviews?.map((review: any) => (
                      <div
                        key={review.id}
                        className="border-b border-border last:border-0 pb-4 last:pb-0"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <ThumbsUp className="w-4 h-4 text-primary mr-2" />
                            <span className="font-medium">{review.user?.full_name || 'Anonymous User'}</span>
                          </div>
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="ml-1">{review.rating}/5</span>
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {review.comment}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}

                    <ReviewForm onSubmit={handleSubmitReview} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Select an expert to view details and book a consultation
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertConnectionPage;